import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
// `Map as MapIcon`: the bare name shadows the global `Map` constructor, which
// this file needs for the replay-key store below.
import {
  BellRing,
  ConciergeBell,
  Map as MapIcon,
  RefreshCw,
  TriangleAlert,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { SERVICE_REQUEST_TRANSITIONS, canTransition } from '@/lib/domain';
import {
  OrderStatus,
  ServiceRequestStatus,
  TableState,
  VENUE_EVENTS,
  type OrderItemEventPayload,
} from '@/lib/shared-types';
import { api } from '@/lib/api';
import { onRealtimeAuthFailure, realtimeAuthFailure, reconnectRealtime } from '@/lib/realtime';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import {
  applyItemEvent,
  invalidateOrder,
  invalidateOrderLists,
  useListOrdersQuery,
  type OrderListQuery,
} from '@/Redux/Slices/operationsApi';
import {
  liveStates,
  useSpacesTablesQuery,
  type TablesArgs,
  type TablesSnapshot,
} from '@/Redux/Slices/spacesApi';
import { useSpacesRealtime } from '@/Redux/Slices/spacesRealtime';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StationModeToggle, useStationMode } from '@/components/StationMode';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TabPanel, Tabs } from '@/components/ui/tabs';
import {
  SERVICE_CALL_ACTIONS,
  ServiceCallDrawer,
  requestLabel,
  type ServiceCallAction,
  type ServiceRequestRow,
} from './ServiceCallDrawer';
import { useStationQueueQuery, type StationQueueQuery } from './waiterStationApi';
import {
  buildPass,
  itemsAwaiting,
  passOrderCount,
  tableIndex,
  waitedFor,
  type PassAction,
  type PassTrip,
} from './waiterPass';
import { AwaitingConfirmationLane } from './AwaitingConfirmationLane';
import { useAwaitingOrders } from './awaitingOrders';
import { apiMessage, formatDuration, formatQuantity, useBranches, useNow } from './shared';

/**
 * The waiter station — the floor, run from the dashboard.
 *
 * ## What is on it, and what is deliberately not
 *
 * `Tarabeeza-Waiter-App` is the specification, and it is six screens: a floor
 * map, the pass, the call-waiter queue, table detail, order taking and settings.
 * Six screens is a handheld's shape, where everything is one tap from a tab bar.
 * A station screen is watched, not navigated, so this is the two of the six that
 * are useless if you have to go looking for them:
 *
 *  - **The pass.** Food the kitchen has finished and nobody has carried. Grouped
 *    by table, oldest first, with the two writes the line is owed. This is the
 *    bug the handheld's `PassScreen` was built for: the kitchen marks a line
 *    READY, it leaves the KDS board, and until something moves it on the guest's
 *    tracking screen sits on "Ready" for the rest of the evening.
 *  - **The call-waiter queue.** Open service requests with an ageing clock and
 *    the actions the shared state machine allows.
 *
 * Beside them, and much smaller, the floor as **context**: how many tables are
 * occupied, waiting to pay, or being cleaned. Not a floor map — `Spaces/FloorMap`
 * is the floor map, it is a canvas with an editor behind it, and a second copy
 * of it here would be a second thing to keep correct. The strip links to it.
 *
 * Left off on purpose:
 *  - **Order taking.** A flow you enter, not something you watch. It has a
 *    screen already (`Operations/Orders`) and putting a menu, a cart and a
 *    routing picker on a board that has to be readable across a room would cost
 *    the board its whole reason for existing.
 *  - **Table detail, sessions, checks.** The floor map's table dialog and
 *    `Billing/CheckDetail` own those, and each is a page of dense forms.
 *  - **Settings, pairing, PIN login.** Device concerns of a handheld; a browser
 *    session already has an identity.
 *
 * ## Three queues, three tabs, and the counts that make that safe
 *
 * The pass, the orders waiting for a waiter and the call queue were three
 * stacked sections, and at a rush all three were half a screen each — the pass
 * pushed below the fold by an alarm, the queue below that. They are tabs now,
 * one per queue, which is also what makes this screen and `Kitchen` read as the
 * same kind of board rather than two unrelated ones.
 *
 * A tab hides what is behind it, and this app has already paid for that once:
 * `Kitchen` shipped dine-in and takeaway as two tabs and a drink sat unmade
 * because the barista never clicked the other one. Nothing errors, nothing
 * warns. The answer there was to merge the boards and badge every card; the
 * answer here is the strip itself — **every tab carries its own count, and the
 * count is on screen whichever panel is open.** That is the whole licence for
 * putting a queue behind a tab, and it is why the counts are not decoration:
 * take them off and this is the takeaway-tab bug again with different nouns.
 *
 * `AwaitingConfirmationLane` is the sharp case, and its own header explains why
 * it draws itself above the fold: it is an alarm whose failure mode is nobody
 * looking. Its tab therefore carries two things — how many are waiting, and, in
 * the danger tone, how many are past the deadline the venue set — so a waiter
 * working the pass sees a late order without having to suspect one exists. Both
 * numbers come from `useAwaitingOrders`, the same hook and therefore the same
 * cache entry the lane's own cards are drawn from: the badge cannot count
 * different orders from the list it points at, and it costs no second request.
 *
 * Every panel stays MOUNTED, hidden rather than unrendered. The lane owns the
 * `ORDER_CONFIRMED` subscription that puts a new order into that count, so a
 * panel torn down on a tab change would leave the badge deaf — silently, and
 * exactly while somebody is looking at a different tab, which is the one moment
 * the badge is the only thing doing any work.
 *
 * ## Realtime
 *
 * Every subscription below names the room it is actually delivered to, because
 * this platform has repeatedly shipped boards that subscribe to a real event in
 * a room the server never emits it to — live, silent, and indistinguishable from
 * a quiet venue. `realtime.service.ts` is the authority and was read for each:
 *
 * | event | rooms the server emits to | why this screen wants it |
 * |---|---|---|
 * | `ITEM_PREPARING` | `branch:` + `station:` | a recall takes a plate back OFF the pass |
 * | `ITEM_READY` | `branch:` + `station:` | a plate lands on the pass |
 * | `ITEM_PICKED_UP` | `branch:` + `station:` | a colleague collected it |
 * | `ITEM_SERVED` | `branch:` + `station:` | it is down, the trip shrinks |
 * | `ORDER_READY` | `branch:` + `order:` + `session:`/`guest:` + `table:` | a whole order JOINS the pass |
 * | `SERVICE_REQUEST_UPDATED` | `branch:` + `area:` + assignee's `user:` | the queue moved |
 * | `TABLE_STATUS_CHANGED` | `branch:` + `floor:` + `area:` | the floor strip |
 *
 * `branch:` is the one room in every list, and it is the room this screen joins.
 *
 * Three things are **not** subscribed to, each for a checked reason:
 *  - `ORDER_COMPLETED` — `realtime.service.ts` has an `@OnEvent` for it and
 *    nothing in the backend ever emits it. A subscription to it is
 *    indistinguishable from a working one, which is the worst kind.
 *  - `TAKEAWAY_READY` — `KitchenService.syncOrder` derives the order status for
 *    every fulfillment type, so a takeaway bag reaching the pass already emits
 *    `ORDER_READY`. Adding this would be a second invalidation of the same list
 *    for the same fact.
 *  - `ITEM_READY` / `TABLE_STATUS_CHANGED` **in a `table:` room** — the handheld
 *    subscribes to both there and `onOrderItemEvent`/`onTableEvent` target no
 *    table room at all. Those subscriptions have never once fired; this does not
 *    copy them.
 *
 * And nothing here trusts the events alone. `useRealtimeResync` refetches all
 * three reads on every reconnect, because Socket.IO replays rooms and never
 * events: a board that patches from events and never re-reads drifts silently
 * across a blip while still ticking its timers.
 */

/** Where the mode flag lives. Namespaced like `venue.kds.stationId`. */
const MODE_KEY = 'venue.waiterStation.mode';

/**
 * Where the open tab lives, in the same idiom and the same namespace as the
 * mode flag beside it. Exported so the test names one key rather than a string
 * that has to be kept in step with this one.
 *
 * A terminal on the pass wall is reopened mid-shift — a reload, a browser
 * restart overnight, a manager closing the tab by accident — and coming back to
 * the first tab means a waiter who was working the call queue is now looking at
 * the pass and does not necessarily notice. `localStorage` rather than the URL
 * on purpose: `routedTabs.tsx` gives a tab a URL because a tab is a *place* to
 * link to and go back to, and this is the one screen in the app that is never
 * navigated. What it needs is not a link, it is the terminal remembering how it
 * was left, which is the same thing `MODE_KEY` remembers about the same screen.
 */
export const TAB_KEY = 'venue.waiterStation.tab';

type StationTab = 'pass' | 'awaiting' | 'calls';

/**
 * The three queues, and the permission each one is made of.
 *
 * The pass first because it is what the terminal is bolted to the wall for, and
 * because it is where an unattended screen should come back to when it has been
 * given no instructions. The alarm second rather than first: its old place at
 * the top of the screen was how it was seen, and its badge is how it is seen
 * now — position no longer carries that, so the reading order can be the order
 * of the work instead.
 *
 * The permission is per tab and not per screen. The pass IS `GET /orders` and
 * the queue IS `GET /service-requests`; they are two reads, and a role that
 * holds one and not the other used to be refused the whole station because of
 * the half it could not have. A withheld tab is not drawn at all — offering one
 * that answers with a refusal is a click that can only disappoint — and if all
 * of them are withheld the screen says which key opens it rather than showing
 * an empty strip.
 */
const STATION_TABS: readonly { key: StationTab; labelKey: string; permission: string }[] = [
  { key: 'pass', labelKey: 'operations.station.tabs.pass', permission: 'orders.view' },
  { key: 'awaiting', labelKey: 'operations.station.tabs.awaiting', permission: 'orders.view' },
  { key: 'calls', labelKey: 'operations.station.tabs.calls', permission: 'service_requests.view' },
];

/**
 * The remembered tab, refused unless it still names one of the three.
 *
 * Whether the actor may *see* it is decided at render, against the permissions
 * they hold now: a role edited between shifts must not strand a terminal on a
 * tab that no longer exists for it. The try/catch is `StationMode`'s, for the
 * same reason — private browsing throws on access, and a screen that will not
 * render because it could not read a preference is a worse failure than one
 * that forgets which tab was open.
 */
function storedTab(): StationTab | null {
  try {
    const value = localStorage.getItem(TAB_KEY);
    return STATION_TABS.some((entry) => entry.key === value) ? (value as StationTab) : null;
  } catch {
    return null;
  }
}

function storeTab(tab: StationTab): void {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // As above. The screen still works; it just does not remember.
  }
}

/**
 * Station mode here is not only "hide the chrome", it is "and I am four
 * metres away". Rendered at 2560 the pass is correct and unreadable: the type
 * is the 14px it is on a laptop, read at three or four times a laptop's
 * distance. The whole scale is rem-based, so one root font-size reflows it.
 */
const STATION_ROOT_FONT = '20px';

/** The two statuses of a service call that are still somebody's problem, plus escalated. */
const OPEN_STATUSES = [
  ServiceRequestStatus.CREATED,
  ServiceRequestStatus.ESCALATED,
  ServiceRequestStatus.ACCEPTED,
].join(',');

/**
 * When a wait becomes the thing to look at, in minutes.
 *
 * One number for both halves of the screen on purpose: `ServiceRequests.tsx`
 * reddens a call at five minutes, and a pass that used a different threshold
 * would have two clocks on one board disagreeing about what "late" means.
 */
const LATE_MINUTES = 5;

/** The table states a waiter acts on, in the order a waiter acts on them. */
const FLOOR_STATES: TableState[] = [
  TableState.PAYMENT_PENDING,
  TableState.OCCUPIED,
  TableState.CLEANING,
  TableState.RESERVED,
  TableState.AVAILABLE,
];

export default function WaiterStation() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const branches = useBranches();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);
  const now = useNow(1000);
  const mode = useStationMode(MODE_KEY, { rootFontSize: STATION_ROOT_FONT });

  const [branchId, setBranchId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [tab, setTab] = useState<StationTab>(() => storedTab() ?? STATION_TABS[0].key);

  const openTab = useCallback((next: StationTab) => {
    setTab(next);
    storeTab(next);
  }, []);

  const canSeePass = can('orders.view');
  const canWorkPass = can('orders.update');
  const canSeeQueue = can('service_requests.view');
  const canSeeFloor = can('tables.view');

  /**
   * The branch this station is standing in.
   *
   * The page's own filter wins when it is set, because that is the branch whose
   * work is on screen; the global header is the fallback. Both feed the rooms
   * below as well as the reads, so the screen can never be listening to one
   * branch while showing another — the exact failure the kitchen board shipped.
   */
  const effectiveBranchId = branchId || activeBranchId || '';

  const rooms = useMemo(
    () => (effectiveBranchId ? [`branch:${effectiveBranchId}`] : []),
    [effectiveBranchId],
  );

  // ------------------------------------------------------------------ reads

  /**
   * `GET /orders?status=READY` — everything the kitchen has finished and nobody
   * has finished serving.
   *
   * `READY` is an **order** status, and that is the right filter rather than a
   * limitation worked around. `deriveOrderStatus` treats READY,
   * AWAITING_PICKUP, PICKED_UP and SERVED alike as "the kitchen is done" and
   * only walks an order to SERVED once every live line is served — so a tray
   * half-collected is still READY and stays here until the last plate is down.
   * A partly-cooked order is deliberately absent: the kitchen has not called
   * that tray, and sending a waiter for half of it is how food arrives cold in
   * two trips.
   *
   * `refetchOnMountOrArgChange` because the cached rows are worth painting the
   * instant the screen opens and worth trusting only until the revalidation
   * lands.
   */
  const orderParams = useMemo<OrderListQuery>(
    () => ({ status: OrderStatus.READY, branchId: effectiveBranchId, limit: 100 }),
    [effectiveBranchId],
  );
  const orders = useListOrdersQuery(orderParams, {
    skip: !canSeePass,
    refetchOnMountOrArgChange: true,
  });

  /**
   * `GET /tables` — two jobs from one request.
   *
   * The pass needs it to turn `session.tableId` into "T-05 · Main Hall", because
   * a UUID is not an instruction; the floor strip needs the live states. The
   * cache keeps structure and state apart precisely so the second can be refused
   * when it is stale — see `liveStates`.
   */
  const tableParams = useMemo<TablesArgs>(
    () => ({ branchId: effectiveBranchId, isActive: true, limit: 200 }),
    [effectiveBranchId],
  );
  const tables = useSpacesTablesQuery(tableParams, {
    skip: !canSeeFloor,
    refetchOnMountOrArgChange: true,
  });

  /**
   * The awaiting count, from the entry `AwaitingConfirmationLane` is already
   * reading.
   *
   * Not a second request and not a prop the lane calls back with: the hook
   * builds one set of query arguments for both callers, so RTK Query answers
   * the second subscriber from the first's cache entry. Skipped without
   * `orders.view` — the tab it counts for is not drawn either, and `refetch()`
   * on a query nobody subscribed to is a console warning on a screen that runs
   * all night.
   */
  const awaiting = useAwaitingOrders(effectiveBranchId, { skip: !canSeePass });

  const queueParams = useMemo<StationQueueQuery>(
    () => ({ status: OPEN_STATUSES, branchId: effectiveBranchId, limit: 100 }),
    [effectiveBranchId],
  );
  const queue = useStationQueueQuery(queueParams, {
    skip: !canSeeQueue,
    refetchOnMountOrArgChange: true,
  });

  const ordersRefetch = orders.refetch;
  const tablesRefetch = tables.refetch;
  const queueRefetch = queue.refetch;

  /**
   * Re-read everything on screen.
   *
   * Guarded on the permission rather than fired blind: `refetch()` on a query
   * that was skipped has no subscription to refetch, and RTK Query answers that
   * with a console warning on a screen that is meant to run all night.
   */
  const reload = useCallback(() => {
    if (canSeePass) void ordersRefetch();
    if (canSeeFloor) void tablesRefetch();
    if (canSeeQueue) void queueRefetch();
  }, [canSeePass, canSeeFloor, canSeeQueue, ordersRefetch, tablesRefetch, queueRefetch]);

  // --------------------------------------------------------------- realtime

  const patchItem = useCallback(
    (payload: OrderItemEventPayload) => {
      dispatch(applyItemEvent(payload));
    },
    [dispatch],
  );

  /**
   * An order **joins** the pass, and there is nothing local that could know it.
   *
   * The payload is an order event: no items, no table, nothing `applyItemEvent`
   * could patch into a list this order is not yet in. This is the one case that
   * costs a request, and it is unavoidable rather than lazy.
   */
  const refreshOrders = useCallback(() => {
    dispatch(invalidateOrderLists());
  }, [dispatch]);

  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PREPARING, patchItem, rooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_READY, patchItem, rooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PICKED_UP, patchItem, rooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_SERVED, patchItem, rooms);
  useRealtime(VENUE_EVENTS.ORDER_READY, refreshOrders, rooms);

  /**
   * The queue, from the branch room.
   *
   * `onServiceRequestEvent` targets branch + the request's area + the assignee's
   * private user room, so a venue-wide queue does repaint from the branch alone
   * — a waiter does not have to be the assignee to see a call age.
   *
   * `SERVICE_REQUEST_UPDATED` only, though `WAITER_REQUESTED` and
   * `BILL_REQUESTED` reach the same rooms: `performCreate` emits the typed event
   * **and** the generic one for every new call, so subscribing to both would be
   * two refetches of one list for one fact.
   */
  const refreshQueue = useCallback(() => {
    if (canSeeQueue) void queueRefetch();
  }, [canSeeQueue, queueRefetch]);
  useRealtime(VENUE_EVENTS.SERVICE_REQUEST_UPDATED, refreshQueue, rooms);

  /**
   * The floor strip's own events, and the connection state the whole screen
   * reports.
   *
   * `useSpacesRealtime` is mounted rather than reimplemented: it already owns
   * `TABLE_STATUS_CHANGED` → the shared cache, the ref-counted connection watch
   * that marks live state unverified when the socket drops, and the resync that
   * clears it. A second listener here would be a second opinion about the same
   * table for the floor map two clicks away to disagree with.
   */
  const { connected } = useSpacesRealtime({ rooms });

  useRealtimeResync(reload);

  /**
   * Realtime has given up and the screen says so instead of looking live.
   *
   * `connectRealtime` stops retrying after three consecutive refused handshakes.
   * A station screen sitting on a stale pass with nothing to say why is the
   * failure this whole screen exists to avoid.
   */
  const realtimeDown = useSyncExternalStore(
    onRealtimeAuthFailure,
    realtimeAuthFailure,
    realtimeAuthFailure,
  );

  const live = connected && !realtimeDown;

  // ------------------------------------------------------------------- pass

  const labels = useMemo(() => tableIndex(tables.data?.tables ?? []), [tables.data?.tables]);
  const trips = useMemo(
    () => buildPass(orders.data?.rows ?? [], labels, now),
    [orders.data?.rows, labels, now],
  );
  const readyOrders = passOrderCount(trips);
  const lateTrips = trips.filter((trip) => isLate(trip.waitingMs)).length;

  /**
   * One transition, for every line of one trip that is owed it.
   *
   * **n writes, not one.** There is no bulk pickup or serve route — every
   * mutation in the kitchen module is single-item — so serving a table of four
   * is four requests. That is the honest shape: if the browser dies after the
   * second, two plates are recorded as served and two are not, which is true.
   *
   * Sequential rather than `Promise.all`, so a pickup fired before a serve is
   * also *sent* before it, and so a partial failure stops rather than racing.
   *
   * The write goes through the axios client, not a cache mutation: `baseApi`
   * says in its own header that it is deliberately not a write path for orders.
   * What it reports afterwards is a tag, so the same order on the Orders screen
   * and on the kitchen board repaints from one invalidation.
   */
  const advance = useCallback(
    async (trip: PassTrip, action: PassAction) => {
      const items = itemsAwaiting(trip, action);
      if (!items.length || busy) return;

      const key = `${action}:${trip.key}`;
      setBusy(key);
      const touched = new Set<string>();
      let failure: unknown = null;

      for (const item of items) {
        try {
          await api.post(`/kitchen/items/${item.id}/${action}`);
          touched.add(item.orderId);
        } catch (error) {
          failure = error;
          break;
        }
      }

      for (const orderId of touched) dispatch(invalidateOrder(orderId));
      if (failure) toast.error(apiMessage(failure, t('operations.errors.actionFailed')));
      else {
        toast.success(
          t(`operations.kitchen.itemMoved.${action === 'pickup' ? 'PICKED_UP' : 'SERVED'}`),
        );
      }
      setBusy(null);
    },
    [busy, dispatch, t],
  );

  // ------------------------------------------------------------------ queue

  const calls = useMemo(() => queue.data ?? [], [queue.data]);
  const openCall = useMemo(
    () => calls.find((row) => row.id === openCallId) ?? null,
    [calls, openCallId],
  );

  /**
   * §28 — one replay key per intended **operation**, not per click.
   *
   * `withOperationId` and the `X-Operation-Id` header make these routes
   * replay-safe, and a one-button card on a board somebody presses while walking
   * past is exactly where a double press happens. Keyed on request + action and
   * held until the call succeeds, so the second press is answered from the
   * first's record instead of acting twice — a fresh id per click would carry
   * the header and none of the protection.
   */
  const operationIds = useRef(new Map<string, string>());
  const operationIdFor = useCallback((key: string): string => {
    const existing = operationIds.current.get(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    operationIds.current.set(key, id);
    return id;
  }, []);

  const runCall = useCallback(
    async (action: ServiceCallAction, row: ServiceRequestRow) => {
      const key = `${action}:${row.id}`;
      const entry = SERVICE_CALL_ACTIONS.find((candidate) => candidate.action === action);
      setBusy(key);
      try {
        await api.post(
          `/service-requests/${row.id}/${action}`,
          {},
          { headers: { 'X-Operation-Id': operationIdFor(key) } },
        );
        operationIds.current.delete(key);
        if (entry) toast.success(t(entry.success));
        void queueRefetch();
      } catch (error) {
        toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      } finally {
        setBusy(null);
      }
    },
    [operationIdFor, queueRefetch, t],
  );

  // ----------------------------------------------------------------- render

  /**
   * The tabs this actor may actually open, and nothing else on the strip.
   *
   * A tab that renders a refusal is a click that can only disappoint, and on a
   * board somebody presses while walking past it is worse than that: it looks
   * like a queue that is empty. Withheld tabs are absent, which also settles
   * where an unattended terminal lands — the first tab it is allowed to have.
   */
  const visibleTabs = STATION_TABS.filter((entry) => can(entry.permission));

  /**
   * Not a permission this screen can work around.
   *
   * With no tab left there is no station to show — and an empty board would
   * read as a quiet venue, which is the one thing this screen must never say
   * when it is not true. Say which key is missing instead. `orders.view` is the
   * one named because it is the key that opens the pass, and the pass is what
   * this screen is for.
   */
  if (!visibleTabs.length) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">
          {t('operations.station.title')}
        </h1>
        <EmptyState
          title={t('operations.station.denied')}
          hint={t('operations.station.deniedHint', {
            permission: 'orders.view',
          })}
        />
      </div>
    );
  }

  /**
   * The remembered tab is a preference, not a promise. A role edited between
   * shifts, or a terminal signed into by somebody else, can name a tab that is
   * no longer on the strip — so what is drawn is decided against the tabs that
   * exist right now, and the fallback is the first of them rather than nothing.
   */
  const current = visibleTabs.some((entry) => entry.key === tab) ? tab : visibleTabs[0].key;

  const counts: Record<StationTab, number> = {
    pass: readyOrders,
    awaiting: awaiting.rows.length,
    calls: calls.length,
  };

  /**
   * What each tab says without being opened.
   *
   * The count is the whole point of the strip, so every tab carries one — a
   * queue that reports nothing is a queue that has to be visited to be ruled
   * out. Only the awaiting tab carries a second, danger-toned number, and only
   * when there is one to carry: a badge that is always there is furniture, and
   * `Kitchen` makes the same rule for its breached count for the same reason.
   * The lapsed number rather than the total in red, because "three waiting" and
   * "one of them is past the venue's limit" are different instructions.
   *
   * `bdi` on both numbers: a count beside an Arabic label is a Latin digit run
   * in an RTL line, and this file's own header explains what an unisolated run
   * does to the punctuation around it.
   */
  const badgeFor = (key: StationTab) => (
    <>
      <bdi>{counts[key]}</bdi>
      {key === 'awaiting' && awaiting.lapsed > 0 && (
        <Badge tone="danger" className="ms-1.5 gap-1 whitespace-nowrap">
          <Icon as={TriangleAlert} size="xs" aria-hidden="true" />
          <bdi>{`${t('operations.awaiting.lapsed')} · ${awaiting.lapsed}`}</bdi>
        </Badge>
      )}
    </>
  );

  const shows = (key: StationTab) => visibleTabs.some((entry) => entry.key === key);

  const anyFetching = orders.isFetching || queue.isFetching || tables.isFetching;

  return (
    <div ref={mode.ref} className={mode.shellClassName}>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ConciergeBell className="h-5 w-5 shrink-0" aria-hidden="true" />
            {/* `bdi` isolates this heading from the paragraph direction around
                it. It was added when these keys were unpublished and an Arabic
                reader saw the English fallback — a Latin sentence in an RTL
                paragraph has its full stop dragged to the front and its
                interpolated count thrown to the far end. The keys are published
                now, and it stays: `bdi` auto-detects, so Arabic keeps RTL, and
                a venue name or an order number inside the line still cannot
                drag punctuation to the wrong end. */}
            <bdi>{t('operations.station.title')}</bdi>
          </h1>
          <p className="text-sm text-ink-muted">
            <bdi>
              {t('operations.station.subtitle')}
            </bdi>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ConnectionPill live={live} down={!!realtimeDown} />
          {anyFetching && (
            <span className="text-xs text-ink-muted">{t('operations.common.updating')}</span>
          )}
          <Button variant="outline" size="sm" onClick={reload} disabled={anyFetching}>
            <RefreshCw className="h-4 w-4" />
            {t('operations.common.refresh')}
          </Button>
          <StationModeToggle mode={mode} />
        </div>
      </header>

      {/*
        The socket is gone, and everything below stopped being confirmable at
        that moment. This banner is the difference between "nothing is waiting"
        and "nothing is arriving" — and an empty pass is the more dangerous of
        the two to show when it is not true.
      */}
      {!live && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40"
        >
          <span className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
            <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('kds.board.reconnecting')}
            <span className="font-normal">
              <bdi>
                {t('operations.station.staleHint')}
              </bdi>
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              reconnectRealtime();
              reload();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {branches.length > 1 && !mode.active && (
        <Card>
          <CardContent className="max-w-sm pt-5">
            <Field label={t('operations.filters.branch')}>
              <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
                <option value="">{t('operations.filters.allBranches')}</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            </Field>
          </CardContent>
        </Card>
      )}

      {canSeeFloor && <FloorPulse snapshot={tables.data} />}

      {/*
        The counts are the strip's reason for existing — see the header. The
        component is the app's own rather than a fourth hand-rolled row of
        buttons: it owns the roles, the one Tab stop, and the arrow keys that
        follow the writing direction instead of the screen.
      */}
      <Tabs
        tabs={visibleTabs.map((entry) => ({
          key: entry.key,
          label: t(entry.labelKey),
          badge: badgeFor(entry.key),
        }))}
        value={current}
        onChange={openTab}
        label={t('operations.station.title')}
        idPrefix="station"
      />

      {/*
        Every panel is rendered and the closed ones are `hidden`, which is the
        ARIA pattern and, here, load-bearing: the awaiting lane's own socket
        subscriptions are what keep its badge true, and a panel unmounted on a
        tab change would take them with it.
      */}
      {shows('pass') && (
        <TabPanel idPrefix="station" tab="pass" hidden={current !== 'pass'} className="space-y-3">
          {/*
            The tab already names this and counts it, so what is left here is
            the one thing a count cannot say: how much of it is late. Silent
            below the threshold, in the same danger tone and the same shape as
            the breached badge on the kitchen board.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-muted">
              <bdi>
                {t('operations.station.readyOrders', {
                  count: readyOrders,
                })}
              </bdi>
            </span>
            {lateTrips > 0 && (
              <Badge tone="danger" className="gap-1 whitespace-nowrap">
                <Icon as={TriangleAlert} size="xs" aria-hidden="true" />
                <bdi>
                  {t('operations.station.lateTrips', {
                    count: lateTrips,
                    minutes: LATE_MINUTES,
                  })}
                </bdi>
              </Badge>
            )}
          </div>

          {orders.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : orders.error ? (
            <ErrorState
              message={
                (orders.error as { message?: string }).message ??
                t('operations.errors.actionFailed')
              }
              onRetry={() => void ordersRefetch()}
            />
          ) : !trips.length ? (
            <EmptyState
              title={
                live
                  ? t('operations.station.passEmpty')
                  : t('operations.station.passUnknown')
              }
              hint={
                live
                  ? t('operations.station.passEmptyHint')
                  : t('operations.station.staleHint')
              }
            />
          ) : (
            /*
              The pass has the whole width now that it is not sharing it with
              the queue, so it uses it the way the kitchen board does — the same
              breakpoints, because these two screens are read from the same
              distance and a card that is legible on one is legible on the other.
            */
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 [&>*]:min-w-0">
              {trips.map((trip) => (
                <TripCard
                  key={trip.key}
                  trip={trip}
                  busy={busy}
                  canWork={canWorkPass}
                  onAdvance={advance}
                />
              ))}
            </div>
          )}
        </TabPanel>
      )}

      {shows('awaiting') && (
        <TabPanel
          idPrefix="station"
          tab="awaiting"
          hidden={current !== 'awaiting'}
          className="space-y-3"
        >
          {/*
            It shares this screen's branch and its rooms rather than resolving
            its own, so the station can never be listening to one branch while
            showing another — the failure the kitchen board shipped and this
            screen's own comments keep guarding against. `labels` is handed down
            so a waiting order names its table instead of a UUID, using the
            `GET /tables` read the pass has already paid for.
          */}
          <AwaitingConfirmationLane
            branchId={effectiveBranchId}
            rooms={rooms}
            tableLabels={labels}
          />

          {/*
            The lane draws nothing at all when nothing is waiting, which is
            right when it is pinned above somebody else's screen and wrong
            inside a panel somebody just opened: an empty panel is indis-
            tinguishable from a broken one. It gets the pass's answer, including
            the distinction the pass makes — "nothing is waiting" is a claim
            this screen may only make while it is actually receiving.
          */}
          {!awaiting.isLoading && !awaiting.error && !awaiting.rows.length && (
            <EmptyState
              title={
                live
                  ? t('operations.station.awaitingEmpty')
                  : t('operations.station.awaitingUnknown')
              }
              hint={
                live
                  ? t('operations.station.awaitingEmptyHint')
                  : t('operations.station.staleHint')
              }
            />
          )}
        </TabPanel>
      )}

      {shows('calls') && (
        <TabPanel idPrefix="station" tab="calls" hidden={current !== 'calls'} className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <BellRing className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
            <span className="text-sm text-ink-muted">
              <bdi>{t('waiter.requests.pending', { count: calls.length })}</bdi>
            </span>
          </div>

          {queue.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : queue.error ? (
            <ErrorState
              message={
                (queue.error as { message?: string }).message ?? t('operations.errors.actionFailed')
              }
              onRetry={() => void queueRefetch()}
            />
          ) : !calls.length ? (
            <EmptyState
              title={
                live
                  ? t('platform.serviceQueue.empty')
                  : t('operations.station.queueUnknown')
              }
              hint={live ? t('platform.serviceQueue.emptyHint') : undefined}
            />
          ) : (
            /*
              One column of one-line calls, wider than it was in the old
              three-column grid but not a board: a call is a name, a clock and
              two buttons, and stretching it into cards would make the queue
              scroll for no information.
            */
            <ul className="max-w-3xl space-y-2">
              {calls.map((call) => (
                <li key={call.id}>
                  <CallCard
                    call={call}
                    now={now}
                    busy={busy}
                    onOpen={() => setOpenCallId(call.id)}
                    onAction={runCall}
                  />
                </li>
              ))}
            </ul>
          )}
        </TabPanel>
      )}

      <ServiceCallDrawer
        request={openCall}
        now={now}
        busy={busy}
        onAction={(action, row) => void runCall(action, row)}
        onClose={() => setOpenCallId(null)}
      />
    </div>
  );
}

/** Past the threshold, in the same units the two clocks on this screen agree on. */
function isLate(waitingMs: number | null): boolean {
  const waited = waitedFor(waitingMs);
  return !!waited && waited.totalMinutes >= LATE_MINUTES;
}

/**
 * The connection, said out loud.
 *
 * Three states and not two: "up", "the socket dropped and is retrying", and
 * "realtime gave up and will not come back on its own". The last one needs a
 * human to press something, so it cannot be drawn the same as the second.
 */
function ConnectionPill({ live, down }: { live: boolean; down: boolean }) {
  const { t } = useTranslation();
  if (live) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
        <Wifi className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('kds.board.live')}
      </span>
    );
  }
  return (
    <span
      className={
        down
          ? 'inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700'
          : 'inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700'
      }
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {t('kds.board.reconnecting')}
    </span>
  );
}

/**
 * The floor, as four numbers.
 *
 * Context, not a map. What a waiter needs from the floor while working the pass
 * is "is anyone waiting to pay" and "how much of the room is turning" — both are
 * counts, and both are wrong the moment the socket drops, which is why this
 * reads through `liveStates` and shows nothing rather than a stale colour when
 * the snapshot is unverified. The map itself is one link away.
 */
function FloorPulse({ snapshot }: { snapshot: TablesSnapshot | undefined }) {
  const { t } = useTranslation();
  const states = liveStates(snapshot);
  const total = snapshot?.tables.length ?? 0;

  const counts = useMemo(() => {
    const tally: Partial<Record<string, number>> = {};
    for (const state of Object.values(states)) tally[state] = (tally[state] ?? 0) + 1;
    return tally;
  }, [states]);

  const unverified = total > 0 && !Object.keys(states).length;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-5 text-sm">
        <span className="font-medium">
          <bdi>{t('operations.station.floor')}</bdi>
        </span>

        {unverified ? (
          <span className="text-ink-muted">
            {t('operations.station.floorUnverified')}
          </span>
        ) : !total ? (
          <span className="text-ink-muted">{t('waiter.floor.noTables')}</span>
        ) : (
          FLOOR_STATES.filter((state) => counts[state]).map((state) => (
            <span key={state} className="inline-flex items-center gap-1.5">
              <Badge tone={toneForStatus(state)}>{t(`table.state.${state}`)}</Badge>
              <span className="tabular-nums font-medium">{counts[state]}</span>
            </span>
          ))
        )}

        <Link
          to="/floor-map"
          className="ms-auto text-sm text-accent underline underline-offset-2"
        >
          {t('nav.floorMap')}
          <MapIcon className="ms-1 inline h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * One trip.
 *
 * The wait is the loudest thing on the card, in the accent, because the ordering
 * of this board is urgency and a card that does not show why it is first is a
 * card a waiter has to re-derive. Both transitions are drawn, in the order the
 * machine has them, and each is offered only to the lines actually owed it: a
 * table whose plates are all still on the pass shows a live "Picked up" and a
 * dead "Served", and the same table one press later shows the reverse.
 */
function TripCard({
  trip,
  busy,
  canWork,
  onAdvance,
}: {
  trip: PassTrip;
  busy: string | null;
  canWork: boolean;
  onAdvance: (trip: PassTrip, action: PassAction) => void;
}) {
  const { t } = useTranslation();
  const late = isLate(trip.waitingMs);
  const seconds = trip.waitingMs === null ? null : trip.waitingMs / 1000;

  const heading = trip.tableNumber
    ? t('platform.serviceQueue.tableNumber', { number: trip.tableNumber })
    : trip.customerName ||
      t('operations.station.noTable');

  const where = [trip.areaName, trip.takeaway ? t('order.fulfillment.TAKEAWAY') : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="flex h-full flex-col gap-3 rounded-xl border border-accent-line bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* A table number is a letter, a neutral and digits, which an Arabic
              paragraph reorders into `05-T`. `bdi` isolates it where it is drawn,
              which is the only place that knows it is a run of its own. */}
          <h3 className="truncate text-base font-semibold">
            <bdi>{heading}</bdi>
          </h3>
          <p className="truncate text-xs text-ink-muted">
            {where}
            {where && ' · '}
            <bdi>{trip.orderNumbers.join(', ')}</bdi>
          </p>
        </div>
        {seconds !== null && (
          <span
            className={
              late
                ? 'shrink-0 text-sm font-semibold tabular-nums text-red-600 dark:text-red-400'
                : 'shrink-0 text-sm font-semibold tabular-nums text-accent'
            }
            aria-label={t('operations.station.waitingFor', {
              duration: formatDuration(seconds),
            })}
          >
            {formatDuration(seconds)}
          </span>
        )}
      </div>

      <ul className="space-y-1 border-t pt-2 text-sm">
        {trip.items.map((item) => (
          <li key={item.id} className="flex items-baseline justify-between gap-3">
            {/* `2× Latte` is a numeric run, a neutral and a word — three runs an
                Arabic paragraph will reorder into a different, plausible, wrong
                line. Each keeps its own direction. */}
            <span className="min-w-0 truncate">
              <bdi className="tabular-nums">{formatQuantity(item.quantity)}×</bdi>{' '}
              <bdi>{item.name}</bdi>
              {item.notes && <span className="text-ink-muted"> — {item.notes}</span>}
            </span>
            <span className="shrink-0 text-xs text-ink-muted">
              {t(`operations.itemStatus.${item.status}`)}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-auto flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          disabled={!canWork || !trip.toCollect || busy !== null}
          onClick={() => onAdvance(trip, 'pickup')}
        >
          {t('waiter.orders.markPickedUp')}
          {trip.toCollect > 0 && <span className="tabular-nums">{trip.toCollect}</span>}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canWork || !trip.toServe || busy !== null}
          onClick={() => onAdvance(trip, 'serve')}
        >
          {t('waiter.orders.markServed')}
          {trip.toServe > 0 && <span className="tabular-nums">{trip.toServe}</span>}
        </Button>
        {!canWork && (
          <p className="basis-full text-xs text-ink-muted">
            {t('operations.kitchen.blocked.permission', { permission: 'orders.update' })}
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * One service call.
 *
 * Every action comes from `SERVICE_CALL_ACTIONS` and is gated on
 * `SERVICE_REQUEST_TRANSITIONS` — the same machine the service asserts against —
 * rather than a hand-rolled "is it still open". That table exists because this
 * exact card used to offer **Resolve** on a `CREATED` call and the server
 * answered `400 Illegal service request transition: CREATED → DONE`.
 */
function CallCard({
  call,
  now,
  busy,
  onOpen,
  onAction,
}: {
  call: ServiceRequestRow;
  now: number;
  busy: string | null;
  onOpen: () => void;
  onAction: (action: ServiceCallAction, row: ServiceRequestRow) => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();

  const seconds = Math.max(
    0,
    ((call.doneAt ? new Date(call.doneAt).getTime() : now) - new Date(call.createdAt).getTime()) /
      1000,
  );
  const late = seconds >= LATE_MINUTES * 60;

  /**
   * `cancel` is deliberately absent from the card and lives only in the drawer.
   *
   * It is the one action here that destroys a guest's request rather than
   * answering it, and `SERVICE_CALL_ACTIONS` gives it the `danger` variant — so
   * on a card it draws a red button the same size as Accept, on a board somebody
   * presses while walking past. Cancelling a call is a decision that wants the
   * note read first, which is what the drawer is for; the card offers the moves
   * a waiter makes without stopping.
   */
  const available = SERVICE_CALL_ACTIONS.filter(
    (entry) =>
      entry.action !== 'cancel' &&
      can(entry.permission) &&
      canTransition(SERVICE_REQUEST_TRANSITIONS, call.status, entry.to),
  );

  return (
    <div className="rounded-xl border border-accent-line bg-surface p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 text-start underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="block truncate text-sm font-semibold">
            <bdi>
              {call.table
                ? t('platform.serviceQueue.tableNumber', { number: call.table.number })
                : requestLabel(call, t)}
            </bdi>
          </span>
          <span className="block truncate text-xs text-ink-muted">
            {[call.area?.name, call.table ? requestLabel(call, t) : null].filter(Boolean).join(' · ')}
          </span>
        </button>
        <span
          className={
            late
              ? 'shrink-0 text-sm font-semibold tabular-nums text-red-600 dark:text-red-400'
              : 'shrink-0 text-sm tabular-nums text-ink-muted'
          }
        >
          {formatDuration(seconds)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge tone={toneForStatus(call.status)}>
          {t(`platform.serviceQueue.statusLabel.${call.status}`)}
        </Badge>
        {available.map((entry) => (
          <Button
            key={entry.action}
            size="sm"
            variant={entry.variant}
            disabled={busy === `${entry.action}:${call.id}`}
            onClick={() => onAction(entry.action, call)}
          >
            {t(entry.label)}
          </Button>
        ))}
        {!SERVICE_REQUEST_TRANSITIONS[call.status].length && (
          <span className="text-xs text-ink-muted">{t('platform.serviceQueue.terminal')}</span>
        )}
      </div>
    </div>
  );
}
